import React, {useCallback, useEffect, useState} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {useAuth} from "../../../providers/AuthenticationProvider";
import {useCustomers} from "../../../providers/CustomerProvider";
import {Button, Confirm, Dropdown, Form} from "semantic-ui-react";
import {useEquipment} from "../../../providers/EquipmentProvider";
import {
    Grid,
    GridColumn as Column,
    GridToolbar,
} from "@progress/kendo-react-grid";
import {equipmentService} from "../../../services/equipmentService";

const WEB_PAGE = 'Equipment List';

export const EquipmentList = () => {
    const { checkAuth, getUserType } = useAuth();
    const {customersList, getAllCustomers, selectedCustomer, setSelectedCustomer} = useCustomers();
    const { getList, equipmentList, total, pageIndex, setPageIndex, pageSize } = useEquipment();
    const [loading, setLoading] = useState(true);
    const [buildingList, setBuildingList] = useState([]);
    const [buildingTotal, setBuildingTotal] = useState(0);
    const [buildingPageCount, setBuildingPageCount] = useState(0);
    const [spaceList, setSpaceList] = useState([]);
    const [spaceTotal, setSpaceTotal] = useState(0);
    const [spacePageCount, setSpacePageCount] = useState(0);
    const [error, setError] = useState(null);
    const [equipmentDeleteConfirmationId, setEquipmentDeleteConfirmationId] = useState(null);
    const { isSystemAdmin } = useAuth();
    const navigate = useNavigate();

    checkAuth(getUserType(), WEB_PAGE) && navigate('/');

    useEffect(() => {
        getAllCustomers();
    }, [getAllCustomers]);

    useEffect(() => {
        if (customersList.length === 1) {
            setSelectedCustomer(customersList[0].id);
        }
    }, [customersList, setSelectedCustomer]);

    const getBuildings = async () => {
        const result = await equipmentService.getBuildingList(selectedCustomer);
        if (result.error) {
            setError(result.message);
        } else {
            setBuildingList(result.data);
            setBuildingPageCount(result.page_count);
            setBuildingTotal(result.total);
        }
    }

    const getSpaces = async () => {
        const result = await equipmentService.getSpaceList(selectedCustomer);
        if (result.error) {
            setError(result.message);
        } else {
            setSpaceList(result.data);
            setSpacePageCount(result.page_count);
            setSpaceTotal(result.total);
        }
    }

    useEffect(() => {
        if (selectedCustomer !== null)
        {
            getList(selectedCustomer);
            getBuildings();
            getSpaces();
        }
    }, [selectedCustomer, getList, pageIndex, pageSize]);

    const onPageChange = (event) => {
        setPageIndex(event.page.skip / event.page.take);
    }

    const onBuildingPageChange = (event) => {
        setPageIndex(event.page.skip / event.page.take);
    }

    //hidden for now at Dan's request
    // const handleDashboardButton = (equipmentId) => {
    //     navigate(`/equipment/${equipmentId}/dashboard`);
    // }

    const handleReportButton = (equipmentId) => {
        navigate(`/equipment/${equipmentId}/report`);
    }

    const handleEditButton = (equipmentType, equipmentId) => {
        navigate(`/${equipmentType}/${equipmentId}`);
    }

    const handleEquipmentDelete = async () => {
        const result = await equipmentService.deleteEquipment(equipmentDeleteConfirmationId);
        if (result.error) {
            setError(result.message);
        } else {
            setEquipmentDeleteConfirmationId(null);
            getList(selectedCustomer);
            getBuildings();
            getSpaces();
        }
    }

    const getEquipmentName = useCallback(equipmentId => {
        const completeList = [...equipmentList, ...buildingList, ...spaceList];
        return completeList.find(x => x.id === equipmentId)?.name || '';
    }, [equipmentList, buildingList, spaceList]);

    return (
        <div>
            <div className='pageHeader' style={{ maxWidth: '1200px '}}>
                <h1>Equipment</h1>
                <Form style={{minWidth: '100px'}}>
                    <Form.Group  style={{ marginBottom: '0' }}>
                        <Form.Field
                            placeholder={'Select Customer'}
                            label={'Customer'}
                            control={Dropdown}
                            fluid
                            selection
                            options={customersList.map(x => {
                                const text =
                                    x?.firstname && x?.lastname
                                        ? `${x?.companyName} (${x?.firstname} ${x?.lastname})`
                                        : x?.companyName;
                                return {
                                    key: x?.id,
                                    text: text,
                                    value: x?.id,
                                };
                            })}
                            value={selectedCustomer}
                            style={{height: '38px', minWidth: '300px'}}
                            onChange={(e, selected) => {
                                const customer = customersList.find(x => x.id === selected.value);
                                console.log('customer.id', customer.id);
                                setSelectedCustomer(customer.id);
                            }}
                            width={16}
                            search
                        />
                    </Form.Group>
                </Form>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '1200px' }}>
                <Grid
                    data={buildingList}
                    pageable={true}
                    sortable={true}
                    total={buildingTotal}
                    onPageChange={onBuildingPageChange}
                    skip={pageIndex * pageSize}
                    take={pageSize}
                >
                    {
                        isSystemAdmin() ? (
                            <GridToolbar>
                                <Button
                                    type={'button'}
                                    size={'small'}
                                    onClick={() => navigate('/building/create')}
                                >
                                    Create Building
                                </Button>
                            </GridToolbar>
                        ) : null
                    }
                    <Column
                        field={'id'}
                        title={'Building ID'}
                    />
                    <Column
                        field={'num'}
                        title={'Num'}
                    />
                    <Column
                        field={'name'}
                        title={'Building Name'}
                    />
                    <Column
                        cell={props => (
                            <td>
                                { isSystemAdmin() ? (
                                    <>
                                        <Button
                                            type={'button'}
                                            onClick={() => handleEditButton('building', props.dataItem.id)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            type={'button'}
                                            onClick={() => setEquipmentDeleteConfirmationId(props.dataItem.id)}
                                        >
                                            Delete
                                        </Button>
                                    </>
                                ) : null}
                            </td>
                        )}
                    />
                </Grid>
                <Grid
                    data={spaceList}
                    pageable={true}
                    sortable={true}
                    total={spaceTotal}
                    onPageChange={onBuildingPageChange}
                    skip={pageIndex * pageSize}
                    take={pageSize}
                >
                    {
                        isSystemAdmin() ? (
                            <GridToolbar>
                                <Button
                                    type={'button'}
                                    size={'small'}
                                    onClick={() => navigate('/space/create')}
                                >
                                    Create Space
                                </Button>
                            </GridToolbar>
                        ) : null
                    }
                    <Column
                        field={'id'}
                        title={'Space ID'}
                    />
                    <Column
                        field={'num'}
                        title={'Num'}
                    />
                    <Column
                        field={'name'}
                        title={'Space Name'}
                    />
                    <Column
                        cell={props => (
                            <td>
                                { isSystemAdmin() ? (
                                    <>
                                        <Button
                                            type={'button'}
                                            onClick={() => handleEditButton('space', props.dataItem.id)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            type={'button'}
                                            onClick={() => setEquipmentDeleteConfirmationId(props.dataItem.id)}
                                        >
                                            Delete
                                        </Button>
                                    </>
                                ) : null}
                            </td>
                        )}
                    />
                </Grid>
                <Grid
                    data={equipmentList}
                    pageable={true}
                    sortable={true}
                    total={total}
                    onPageChange={onPageChange}
                    skip={pageIndex * pageSize}
                    take={pageSize}
                >
                    {
                        isSystemAdmin() ? (
                            <GridToolbar>
                                <Button
                                    type={'button'}
                                    size={'small'}
                                    onClick={() => navigate('/equipment/create')}
                                >
                                    Create Equipment
                                </Button>
                            </GridToolbar>
                        ) : null
                    }
                    <Column
                        field={'id'}
                        title={'Equipment ID'}
                    />
                    <Column
                        field={'num'}
                        title={'Num'}
                    />
                    <Column
                        field={'name'}
                        title={'Equipment Name'}
                    />
                    <Column
                        cell={props => (
                            <td>
                                {/*hidden for now at Dan's request*/}
                                {/*{ isSystemAdmin() ? (*/}
                                {/*    <Button*/}
                                {/*        type={'button'}*/}
                                {/*        onClick={() => handleDashboardButton(props.dataItem.id)}*/}
                                {/*    >*/}
                                {/*        Dashboard*/}
                                {/*    </Button>*/}
                                {/*    ) : null*/}
                                {/*}*/}
                                <Button
                                    type={'button'}
                                    onClick={() => handleReportButton(props.dataItem.id)}
                                >
                                    Report
                                </Button>
                                { isSystemAdmin() ? (
                                    <>
                                        <Button
                                            type={'button'}
                                            onClick={() => handleEditButton('equipment', props.dataItem.id)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            type={'button'}
                                            onClick={() => setEquipmentDeleteConfirmationId(props.dataItem.id)}
                                        >
                                            Delete
                                        </Button>
                                    </>
                                ) : null}
                            </td>
                        )}
                    />
                </Grid>
            </div>
            <Confirm
                open={equipmentDeleteConfirmationId !== null}
                onCancel={() => setEquipmentDeleteConfirmationId(null)}
                onConfirm={handleEquipmentDelete}
                content={`Are you sure you want to delete equipment ${getEquipmentName(equipmentDeleteConfirmationId)} (ID: ${equipmentDeleteConfirmationId})?`}
                confirmButton="Confirm"
            />
        </div>
    );
};
